import { Component, DestroyRef, effect, inject, input, output, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { CellRangeEventArgs, Column, DataMap, FlexGrid } from '@grapecity/wijmo.grid'

import { BaseComponent, Division, ForemanRosterEntry, Grid, ListItem } from '../../../shared'
import { ActionToolbarCount } from '../../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { AutoSizeDirective } from '../../../shared/utility/auto-size.directive'

@Component({
    selector: 'app-foreman-schedule-foreman-tab',
    templateUrl: './foreman-schedule-foreman-tab.component.html',
    styleUrls: ['./foreman-schedule-foreman-tab.component.css'],
    standalone: true,
    imports: [FormsModule, ActionToolbarCount, ActionToolbarSearchComponent, WjGridModule, WjGridFilterModule, AutoSizeDirective],
})
export class ForemanScheduleForemanTabComponent extends BaseComponent {
    private readonly destroyRef = inject(DestroyRef)

    /** When true, loads the roster on first activation. */
    readonly isActive = input(false)
    readonly canEdit = input(true)

    /** Emitted after a foreman flag is successfully changed, so other tabs can reload. */
    readonly foremanChanged = output<void>()

    readonly isLoading = signal(false)
    readonly loadError = signal<string | null>(null)

    grid: Grid<ForemanRosterEntry>

    // All divisions (archived included), used for display so an already-assigned archived division still renders.
    divisionMap = new DataMap([], 'value', 'display')
    // Non-archived divisions plus a "No Division" option, used while editing.
    divisionEditMap = new DataMap([], 'value', 'display')

    private hasLoaded = false

    // Division value captured when an edit begins, used to revert the cell if the save fails.
    private divisionBeforeEdit: string | null | undefined

    constructor() {
        super()

        this.grid = new Grid<ForemanRosterEntry>(this.services, 'hrRef')
            .filterBy(item => [item.name, item.jobTitle, this.divisionMap.getDisplayValue(item.divisionId)])
            .isReadOnly(false)
        this.grid.canEdit = this.canEdit()

        this.grid.initialized.on(g => {
            g.flexGrid.beginningEdit.addHandler((flexGrid: FlexGrid, args: CellRangeEventArgs) =>
                this.onBeginningEdit(flexGrid, args),
            )
            g.flexGrid.cellEditEnded.addHandler((flexGrid: FlexGrid, args: CellRangeEventArgs) =>
                this.onCellEditEnded(flexGrid, args),
            )
        })

        effect(() => {
            if (this.isActive() && !this.hasLoaded) {
                this.loadRoster()
            }
        })
    }

    refresh() {
        this.hasLoaded = false
        this.loadRoster()
    }

    private loadRoster() {
        this.isLoading.set(true)
        this.loadError.set(null)
        this.services.data
            .load(
                false,
                false,
                this.services.data.get<ForemanRosterEntry[]>('api/ForemanSchedule/GetForemanRoster'),
                this.services.data.divisions.queryAll(),
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: result => {
                    const roster = (result[0] as ForemanRosterEntry[]) ?? []
                    const divisions = (result[1] as Division[]) ?? []

                    this.buildDivisionMaps(divisions)

                    this.hasLoaded = true
                    this.isLoading.set(false)
                    this.grid.items = roster
                },
                error: () => {
                    this.isLoading.set(false)
                    this.loadError.set('Failed to load the foreman roster.')
                },
            })
    }

    private buildDivisionMaps(divisions: Division[]) {
        this.divisionMap = new DataMap(
            divisions.map(d => new ListItem<string>(d.id, d.name)),
            'value',
            'display',
        )
        this.divisionEditMap = new DataMap(
            [
                new ListItem<string>('', 'No Division'),
                ...divisions.filter(d => !d.archived).map(d => new ListItem<string>(d.id, d.name)),
            ],
            'value',
            'display',
        )
    }

    private onBeginningEdit(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        const col = flexGrid.columns[args.col] as Column
        if (col.binding !== 'divisionId') return

        if (!this.canEdit()) {
            // No edit rights: cancel the edit outright.
            args.cancel = true
            return
        }

        // While editing, restrict the dropdown to non-archived divisions plus the "No Division" option.
        col.dataMap = this.divisionEditMap
        const entry = flexGrid.rows[args.row].dataItem as ForemanRosterEntry
        this.divisionBeforeEdit = entry?.divisionId
    }

    private onCellEditEnded(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        const binding = flexGrid.columns[args.col].binding

        if (binding === 'divisionId') {
            // Restore the full division map so archived assignments still render after editing.
            ;(flexGrid.columns[args.col] as Column).dataMap = this.divisionMap

            const entry = flexGrid.rows[args.row].dataItem as ForemanRosterEntry
            if (!entry) return
            this.saveDivision(entry)
            return
        }

        if (binding !== 'isForeman') return

        const entry = flexGrid.rows[args.row].dataItem as ForemanRosterEntry
        if (!entry) return

        if (!this.canEdit()) {
            // Revert any edit attempted without edit rights.
            entry.isForeman = !entry.isForeman
            this.grid.refresh(true)
            return
        }

        this.saveFlag(entry)
    }

    private saveFlag(entry: ForemanRosterEntry) {
        const desired = entry.isForeman
        this.services.data
            .put<ForemanRosterEntry>('api/ForemanSchedule/SaveForemanFlag', { hrRef: entry.hrRef, isForeman: desired })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    const label = desired ? 'marked as a foreman' : 'removed as a foreman'
                    this.services.notification.success(`${entry.name} ${label}.`)
                    this.foremanChanged.emit()
                },
                error: () => {
                    entry.isForeman = !desired
                    this.grid.refresh(true)
                    this.services.notification.error(`Failed to update ${entry.name}.`)
                },
            })
    }

    private saveDivision(entry: ForemanRosterEntry) {
        const previous = this.divisionBeforeEdit
        const desired = entry.divisionId || null
        if ((previous || null) === desired) return

        this.services.data
            .put<void>('api/ForemanSchedule/SaveForemanDivision', { hrRef: entry.hrRef, divisionId: desired })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    const label = desired ? `assigned to ${this.divisionMap.getDisplayValue(desired)}` : 'set to No Division'
                    this.services.notification.success(`${entry.name} ${label}.`)
                },
                error: () => {
                    entry.divisionId = previous ?? null
                    this.grid.refresh(true)
                    this.services.notification.error(`Failed to update the division for ${entry.name}.`)
                },
            })
    }
}
